import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "./cart";
import { ProductGrid, ConditionBadge, TrustBanner } from "./components";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronDown, SlidersHorizontal, X } from "lucide-react";

const FILTERS = [["category", "categories"], ["brand", "brands"], ["appliance", "appliances"]];
const SORTS = ["relevance", "price_asc", "price_desc", "newest", "availability"];

const chip = (on) => `inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 text-sm font-medium transition-colors ${on ? "border-emerald-700 bg-emerald-700 text-white" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"}`;

function Pill({ label, on, testId, children }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild><button type="button" data-testid={testId} className={chip(on)}>{label}<ChevronDown className="h-3.5 w-3.5 opacity-70" /></button></PopoverTrigger>
      <PopoverContent align="start" className="sl-store w-64 rounded-xl border-slate-200 bg-white p-2 text-slate-900 shadow-xl">{typeof children === "function" ? children(() => setOpen(false)) : children}</PopoverContent>
    </Popover>
  );
}

function FacetList({ fk, keyName, facets, params, set }) {
  return (
    <div className="max-h-72 space-y-0.5 overflow-y-auto">
      {facets[fk].slice(0, 12).map((f) => {
        const on = params.get(keyName) === f.value;
        return <button key={f.value} data-testid={`filter-${keyName}-${f.value}`} onClick={() => set({ [keyName]: on ? null : f.value })} className={`flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm ${on ? "bg-emerald-50 font-medium text-emerald-800" : "text-slate-700 hover:bg-slate-100"}`}><span className="truncate">{f.value}</span><span className="text-xs text-slate-400">{f.count}</span></button>;
      })}
    </div>
  );
}

function ConditionList({ facets, params, set }) {
  return (
    <div className="flex flex-wrap gap-1.5 p-1">
      {facets.conditions.map((c) => { const on = params.get("condition") === c.value; return <button key={c.value} data-testid={`filter-condition-${c.value}`} onClick={() => set({ condition: on ? null : c.value })} className={`rounded-full ring-offset-1 ${on ? "ring-2 ring-emerald-600" : ""}`}><ConditionBadge condition={c.value} /></button>; })}
    </div>
  );
}

function PriceForm({ facets, params, set, st, onDone }) {
  const [price, setPrice] = useState({ min: params.get("min_price") || "", max: params.get("max_price") || "" });
  return (
    <form onSubmit={(e) => { e.preventDefault(); set({ min_price: price.min || null, max_price: price.max || null }); onDone?.(); }} className="flex items-center gap-2 p-1">
      <input data-testid="filter-min-price" type="number" placeholder={`$${facets?.price_min ?? 0}`} value={price.min} onChange={(e) => setPrice({ ...price, min: e.target.value })} className="h-9 w-full rounded-md border border-slate-300 px-2 text-sm" />
      <span className="text-slate-400">–</span>
      <input data-testid="filter-max-price" type="number" placeholder={`$${facets?.price_max ?? 0}`} value={price.max} onChange={(e) => setPrice({ ...price, max: e.target.value })} className="h-9 w-full rounded-md border border-slate-300 px-2 text-sm" />
      <button type="submit" data-testid="filter-price-apply" className="h-9 rounded-md bg-emerald-700 px-3 text-sm font-medium text-white">{st("apply")}</button>
    </form>
  );
}

function SortList({ params, set, st }) {
  const cur = params.get("sort") || "relevance";
  return (
    <div className="space-y-0.5" data-testid="sort-select">
      {SORTS.map((s) => <button key={s} type="button" data-testid={`sort-${s}`} onClick={() => set({ sort: s })} className={`flex w-full rounded-md px-2 py-1.5 text-left text-sm ${cur === s ? "bg-emerald-50 font-medium text-emerald-800" : "text-slate-700 hover:bg-slate-100"}`}>{st(s)}</button>)}
    </div>
  );
}

function FilterBar({ facets, params, set, st, lang }) {
  const avail = params.get("available_only") === "true";
  const priceOn = params.get("min_price") || params.get("max_price");
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="store-filters">
      {FILTERS.map(([key, fk]) => facets?.[fk]?.length > 0 && (
        <Pill key={key} label={params.get(key) || st(key)} on={!!params.get(key)} testId={`filter-pill-${key}`}><FacetList fk={fk} keyName={key} facets={facets} params={params} set={set} /></Pill>
      ))}
      {facets?.conditions?.length > 0 && <Pill label={st("condition")} on={!!params.get("condition")} testId="filter-pill-condition"><ConditionList facets={facets} params={params} set={set} /></Pill>}
      <Pill label={priceOn ? `$${params.get("min_price") || facets?.price_min || 0} – $${params.get("max_price") || facets?.price_max || ""}` : st("price")} on={!!priceOn} testId="filter-pill-price">{(close) => <PriceForm facets={facets} params={params} set={set} st={st} onDone={close} />}</Pill>
      <label className={`${chip(avail)} cursor-pointer`}><input data-testid="filter-available-only" type="checkbox" className="sr-only" checked={avail} onChange={(e) => set({ available_only: e.target.checked ? "true" : null })} />{lang === "es" ? "Solo disponibles" : "Available only"}</label>
      <span className="ml-auto" />
      <Pill label={`${st("sort")}: ${st(params.get("sort") || "relevance")}`} on={false} testId="filter-pill-sort"><SortList params={params} set={set} st={st} /></Pill>
    </div>
  );
}

function MobileFilters({ facets, params, set, st, lang }) {
  const Section = ({ title, children }) => <div className="border-b border-slate-200 py-4 last:border-0"><div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</div>{children}</div>;
  return (
    <div data-testid="store-filters-mobile">
      {FILTERS.map(([key, fk]) => facets?.[fk]?.length > 0 && <Section key={key} title={st(key)}><FacetList fk={fk} keyName={key} facets={facets} params={params} set={set} /></Section>)}
      {facets?.conditions?.length > 0 && <Section title={st("condition")}><ConditionList facets={facets} params={params} set={set} /></Section>}
      <Section title={st("price")}><PriceForm facets={facets} params={params} set={set} st={st} /></Section>
      <Section title={lang === "es" ? "Disponibilidad" : "Availability"}><label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={params.get("available_only") === "true"} onChange={(e) => set({ available_only: e.target.checked ? "true" : null })} />{lang === "es" ? "Solo disponibles" : "Available only"}</label></Section>
      <Section title={st("sort")}><SortList params={params} set={set} st={st} /></Section>
    </div>
  );
}

function pageTitle(params, st, lang) {
  const q = params.get("q"), model = params.get("model"), sort = params.get("sort");
  if (q) return `“${q}”`;
  if (model) return `${lang === "es" ? "Modelo" : "Model"} ${model}`;
  if (params.get("appliance")) return params.get("appliance");
  if (params.get("category")) return params.get("category");
  if (params.get("brand")) return params.get("brand");
  if (sort === "newest") return st("newest_parts");
  if (sort === "availability" || params.get("available_only") === "true") return lang === "es" ? "Piezas disponibles" : "Available parts";
  return lang === "es" ? "Todas las piezas" : "All parts";
}

export default function StoreSearch() {
  const { st, lang } = useCart();
  const [error, setError] = useState(false);
  const [params, setParams] = useSearchParams();
  const [res, setRes] = useState(null);
  const [facets, setFacets] = useState(null);
  const [sheet, setSheet] = useState(false);
  const q = params.get("q") || "";
  useEffect(() => {
    const qs = params.toString();
    setRes(null); setError(false);
    Promise.all([api.get(`/store/search?${qs}`), api.get(`/store/facets?q=${encodeURIComponent(q)}`)]).then(([r, f]) => { setRes(r.data); setFacets(f.data); }).catch(() => setError(true));
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (patch) => { const p = new URLSearchParams(params); Object.entries(patch).forEach(([k, v]) => (v == null || v === "" ? p.delete(k) : p.set(k, v))); if (!("page" in patch)) p.delete("page"); setParams(p); };
  const active = ["category", "brand", "appliance", "condition", "min_price", "max_price", "model", "available_only"].filter((k) => params.get(k));
  const page = parseInt(params.get("page") || "1", 10);
  return (
    <div data-testid="store-search-page">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900 sm:text-3xl" data-testid="search-title">{pageTitle(params, st, lang)}</h1>
          {res && <p className="mt-1 text-sm text-slate-500" data-testid="results-count">{res.total} {st("results")}</p>}
        </div>
        <button data-testid="mobile-filters-btn" onClick={() => setSheet(true)} className={`${chip(active.length > 0)} lg:hidden`}><SlidersHorizontal className="h-4 w-4" />{st("filters")}{active.length ? ` (${active.length})` : ""}</button>
      </div>
      <div className="mb-5 hidden lg:block">{facets && <FilterBar facets={facets} params={params} set={set} st={st} lang={lang} />}</div>
      {page === 1 && <div className="mb-5"><TrustBanner items={["tested", "reuse"]} testId="search-trust-banner" /></div>}
      {active.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="active-filters">
          {active.map((k) => <button key={k} onClick={() => set({ [k]: null })} className="flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-800 hover:bg-emerald-100">{st(k) !== k ? st(k) : k}: {params.get(k)} <X className="h-3 w-3" /></button>)}
          <button data-testid="clear-filters" onClick={() => setParams(q ? { q } : {})} className="text-xs font-medium text-slate-500 underline">{st("clear")}</button>
        </div>
      )}
      {error && <p role="alert" data-testid="search-error" className="rounded-lg bg-red-50 p-6 text-red-700">{lang === "es" ? "No se pudo cargar la búsqueda. Inténtalo de nuevo." : "Search could not be loaded. Please try again."}</p>}
      {!res && !error && <div className="grid grid-cols-2 gap-3 md:grid-cols-[repeat(auto-fill,minmax(min(100%,17rem),1fr))] md:gap-5">{[...Array(8)].map((_, i) => <div key={i} className="h-64 animate-pulse rounded-xl bg-slate-200 md:h-80 md:rounded-2xl" />)}</div>}
      {res && res.items.length === 0 && <div className="rounded-xl border border-dashed border-slate-300 bg-white py-20 text-center" data-testid="no-results"><div className="font-heading text-lg text-slate-700">{st("no_results")}</div><div className="mt-1 text-sm text-slate-500">{st("try")}</div></div>}
      {res && res.items.length > 0 && <ProductGrid items={res.items} />}
      {res && res.pages > 1 && (
        <div className="mt-8 flex items-center justify-center gap-2" data-testid="pagination">
          {[...Array(res.pages)].map((_, i) => <button key={i} onClick={() => set({ page: String(i + 1) })} className={`h-9 w-9 rounded-md text-sm font-medium ${page === i + 1 ? "bg-emerald-700 text-white" : "border border-slate-300 bg-white hover:border-emerald-600"}`}>{i + 1}</button>)}
        </div>
      )}
      <Sheet open={sheet} onOpenChange={setSheet}>
        <SheetContent side="bottom" className="sl-store max-h-[85vh] overflow-y-auto rounded-t-2xl bg-white text-slate-900" data-testid="mobile-filters-sheet">
          <SheetHeader><SheetTitle className="font-heading text-slate-900">{st("filters")}</SheetTitle></SheetHeader>
          {facets && <MobileFilters facets={facets} params={params} set={set} st={st} lang={lang} />}
          <button onClick={() => setSheet(false)} className="mt-4 w-full rounded-lg bg-emerald-700 py-3 font-medium text-white">{st("apply")}</button>
        </SheetContent>
      </Sheet>
    </div>
  );
}
